import React, { useState, useEffect } from 'react';
import CookieConsent from 'react-cookie-consent';
import { cookieManager, COOKIE_TYPES } from '../utils/cookieManager';

const CookieConsentBanner = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    [COOKIE_TYPES.NECESSARY]: true,
    [COOKIE_TYPES.ANALYTICS]: false,
    [COOKIE_TYPES.MARKETING]: false,
    [COOKIE_TYPES.FUNCTIONAL]: false
  });

  useEffect(() => {
    // Check if consent is expired and needs to be re-requested
    if (cookieManager.hasConsent() && cookieManager.isConsentExpired()) {
      cookieManager.revokeConsent();
    }
  }, []);

  const handleAcceptAll = () => {
    const allConsent = {
      [COOKIE_TYPES.NECESSARY]: true,
      [COOKIE_TYPES.ANALYTICS]: true,
      [COOKIE_TYPES.MARKETING]: true,
      [COOKIE_TYPES.FUNCTIONAL]: true
    };
    cookieManager.setConsent(allConsent);
  };

  const handleAcceptSelected = () => {
    cookieManager.setConsent(preferences);
    setShowDetails(false);
  };

  const handleRejectAll = () => {
    const necessaryOnly = {
      [COOKIE_TYPES.NECESSARY]: true,
      [COOKIE_TYPES.ANALYTICS]: false,
      [COOKIE_TYPES.MARKETING]: false,
      [COOKIE_TYPES.FUNCTIONAL]: false
    };
    cookieManager.setConsent(necessaryOnly);
  };

  const handlePreferenceChange = (type, value) => {
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const cookieInfo = {
    [COOKIE_TYPES.NECESSARY]: {
      title: 'Necessary Cookies',
      description: 'These cookies are essential for the website to function properly. They enable basic features like page navigation and access to secure areas.',
      required: true
    },
    [COOKIE_TYPES.ANALYTICS]: {
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously via Google Analytics.',
      required: false
    },
    [COOKIE_TYPES.FUNCTIONAL]: {
      title: 'Functional Cookies',
      description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.',
      required: false
    },
    [COOKIE_TYPES.MARKETING]: {
      title: 'Marketing Cookies',
      description: 'These cookies are used to track visitors across websites to display relevant advertisements and marketing content.',
      required: false
    }
  };

  if (cookieManager.hasConsent() && !cookieManager.isConsentExpired()) {
    return null;
  }

  return (
    <>
      <CookieConsent
        location="bottom"
        buttonText="Accept All"
        declineButtonText="Reject All"
        enableDeclineButton
        flipButtons
        style={{
          background: 'rgba(255, 255, 255, 0.98)',
          color: '#1f2937',
          boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderBottom: 'none',
          fontSize: '14px',
          padding: '16px 12px',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
        buttonStyle={{
          background: '#af0017',
          color: 'white',
          fontSize: '14px',
          fontWeight: '600',
          borderRadius: '6px',
          border: 'none',
          padding: '12px 32px',
          cursor: 'pointer',
          marginLeft: '10px',
          transition: 'background-color 0.2s'
        }}
        declineButtonStyle={{
          background: '#00246a',
          color: 'white',
          fontSize: '14px',
          fontWeight: '600',
          borderRadius: '6px',
          border: 'none',
          padding: '12px 32px',
          cursor: 'pointer',
          marginLeft: '10px',
          transition: 'background-color 0.2s'
        }}
        onAccept={handleAcceptAll}
        onDecline={handleRejectAll}
        cookieName="terrapin_cookie_banner_dismissed"
        expires={365}
        disableStyles={false}
        overlay={false}
      >
        <div className="max-w-6xl mx-auto px-2 sm:px-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                We use cookies to enhance your experience
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-3">
                We use cookies and similar technologies to provide the best experience on our website. 
                Some cookies are necessary for the site to function, while others help us analyze usage 
                and improve our services. You can customize your preferences or accept all cookies.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-terrapin-blue hover:text-terrapin-red text-sm font-medium underline focus:outline-none focus:ring-2 focus:ring-terrapin-blue focus:ring-offset-2 rounded"
                >
                  {showDetails ? 'Hide Details' : 'Customize Preferences'}
                </button>
                <span className="text-gray-400">•</span>
                <a
                  href="/privacy-policy"
                  className="text-terrapin-blue hover:text-terrapin-red text-sm font-medium underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>

          {showDetails && (
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-[50vh] overflow-y-auto">
              <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Cookie Preferences</h4>
              
              <div className="space-y-3 sm:space-y-4">
                {Object.entries(cookieInfo).map(([type, info]) => (
                  <div key={type} className="flex items-start justify-between p-2 sm:p-3 bg-white rounded border border-gray-200">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="text-sm sm:text-base font-medium text-gray-900">{info.title}</h5>
                        {info.required && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                            Required
                          </span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600">{info.description}</p>
                    </div>
                    
                    <div className="flex-shrink-0 ml-2">
                      {info.required ? (
                        <div className="w-10 h-5 sm:w-12 sm:h-6 bg-terrapin-red rounded-full flex items-center justify-end px-1">
                          <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full"></div>
                        </div>
                      ) : (
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences[type]}
                            onChange={(e) => handlePreferenceChange(type, e.target.checked)}
                            className="sr-only"
                          />
                          <div className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full flex items-center px-1 transition-colors ${
                            preferences[type] ? 'bg-terrapin-red justify-end' : 'bg-gray-300 justify-start'
                          }`}>
                            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full transition-transform"></div>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
                <button
                  onClick={handleAcceptSelected}
                  className="btn-base btn-primary text-sm focus:outline-none focus:ring-2 focus:ring-terrapin-red focus:ring-offset-2"
                >
                  Save Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="btn-base btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-terrapin-blue focus:ring-offset-2"
                >
                  Accept All
                </button>
                <button
                  onClick={handleRejectAll}
                  className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-6 py-2 rounded-md font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-terrapin-red focus:ring-offset-2"
                >
                  Reject All
                </button>
              </div>
            </div>
          )}
        </div>
      </CookieConsent>
    </>
  );
};

export default CookieConsentBanner; 